import * as moment from "moment-timezone";
import { FlagIcon } from "react-flag-kit";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";

import {
  getLevels,
  getClassesRating,
  getClassesRatingById,
  getLingoRatingCriterias,
} from "../../store/middleware/generic.middleware";
import Pagination from "../../components/Pagination";
import Filter from "../../components/elements/Filter";
import Header from "../../components/Header/NewHeader";
import SideMenu from "../../components/SideMenu/SideMenu";
import { decryptIfEncrypted } from "../../utils/fieldDecryptor";
import ModalRating from "../../components/ModalRating/ModalRating";
import Placeholder from "../../components/Placeholder/PlaceholderText";
import ClassRatingIcon from "../../assets/images/icons/icon_classratings_header.svg";
import { endLoading, initLoading, setClassesForRating } from "../../store/slices/generics.slice";

import { Next } from "./styles";

function ClassRatings() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.userReducer);
  const { ratingCriterias, classesForRating } = useSelector((state) => state.genericReducer);

  const dateVerified = "2023-10-19";

  const [targetRating, setTarget] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [classesRatings, setClassesRatings] = useState([]);
  const [pagination, setPagination] = useState({ pageNumber: 0, pageSize: 0, totalFound: 0, totalPages: 0 });
  const [filter, setFilter] = useState({
    planId: "",
    status: "",
    teacher: "",
    student: "",
    levelId: "",
    pageSize: 20,
    pageNumber: 1,
    studentLevel: "",
    lingoLanguageId: "",
    ratingCriteriaId: "",
    classRatingStatus: "",
    ratingCriteriaGrade: "",
    studentId: user.role === "student" ? user.studentId : "",
    endAt: user.role === "student" ? "" : moment().format("YYYY-MM-DD"),
    startAt: user.role === "student" ? "" : moment().subtract(30, "days").format("YYYY-MM-DD"),
  });

  const handlePagination = (page) => {
    if (page.toString() === filter["pageNumber"].toString()) return;

    filter["pageNumber"] = page;
    setFilter(filter);
    callApi(false, filter);
  };

  const callApi = useCallback(
    async (fullLoad = true, filter) => {
      await dispatch(initLoading());

      await dispatch(getClassesRating(filter, true)).then((res) => {
        setClassesRatings(res.items);
        setPagination({
          pageSize: res.pageSize,
          pageNumber: res.pageNumber,
          totalFound: res.totalFound,
          totalPages: res.totalPages,
        });
      });

      if (fullLoad && user.role !== "student") dispatch(getLevels());

      await dispatch(endLoading());
    },
    // eslint-disable-next-line
    [],
  );

  const applyFilters = () => {
    filter["pageNumber"] = 1;
    setFilter(filter);
    callApi(false, filter);
  };

  const clearFilters = (e) => {
    e.preventDefault();

    const payload = {
      status: "",
      planId: "",
      student: "",
      teacher: "",
      levelId: "",
      pageSize: 20,
      pageNumber: 1,
      studentLevel: "",
      lingoLanguageId: "",
      ratingCriteriaId: "",
      classRatingStatus: "",
      ratingCriteriaGrade: "",
      studentId: user.role === "student" ? user.id : "",
      endAt: user.role === "student" ? "" : moment().format("YYYY-MM-DD"),
      startAt: user.role === "student" ? "" : moment().subtract(30, "days").format("YYYY-MM-DD"),
    };

    setFilter(payload);
    callApi(false, payload);
  };

  const handleFilterPanel = (e) => {
    e.preventDefault();
    setExpanded(!expanded);
  };

  const inputChange = (e, name) => {
    const { value } = e.target;
    setFilter((state) => ({ ...state, [name]: value }));
  };

  const openRating = async (e, index) => {
    e.preventDefault();

    const target = "teacher";
    const classesRatingDetail = [];

    if (classesRatings[index]?.teacherRating?.status === "done") {
      await dispatch(getClassesRatingById(classesRatings[index]?.teacherRating?.id)).then((res) =>
        classesRatingDetail.push(res),
      );
    }

    const classesForRating = classesRatings
      .filter((item) => item?.sequentialScheduleId === classesRatings[index].sequentialScheduleId)
      .map((item) => {
        return { ...item, target, classesRatingDetail };
      });

    dispatch(setClassesForRating(classesForRating));
    setTarget(target);
  };

  const verifiedDate = (data1, data2) => {
    const dataObj1 = moment(data1).format("YYYY-MM-DD");
    const dataObj2 = moment(data2).format("YYYY-MM-DD");

    if (dataObj1 >= dataObj2) return true;
  };

  const filters = [
    {
      type: "date",
      name: "startAt",
      placeholder: "Select",
      label: "PERIOD_START",
      value: filter.startAt,
    },
    {
      type: "date",
      name: "endAt",
      label: "PERIOD_END",
      value: filter.endAt,
      placeholder: "Select",
    },
    { name: "teacher", value: filter.teacher, label: "TEACHER", placeholder: "TEACHER_NAME", type: "text" },
    user.role !== "student" && {
      name: "student",
      value: filter.student,
      label: "STUDENT",
      placeholder: "STUDENT_NAME",
      type: "text",
    },
    {
      type: "status",
      label: "Status",
      placeholder: "Select",
      name: "classRatingStatus",
      value: filter.classRatingStatus,
      options: ["Rated", "Pending"]?.map((option, i) => ({ id: i, name: option })),
    },

    expanded &&
      user.role !== "student" && {
        type: "lingoLanguages",
        placeholder: "Select",
        name: "lingoLanguageId",
        label: "Native Language",
        value: filter.lingoLanguageId,
      },
    expanded &&
      user.role !== "student" && {
        type: "levels",
        placeholder: "Select",
        name: "levelId",
        label: "Levels",
        value: filter.levelId,
      },
  ];

  useEffect(() => {
    callApi(true, filter);
    dispatch(getLingoRatingCriterias());
    // eslint-disable-next-line
  }, []);

  return (
    <div className="view">
      <SideMenu />
      <section>
        <Header
          t={t}
          typeIcon="img"
          title={t("CLASS_RATING")}
          initLoadingBoolean={false}
          icon={<img src={ClassRatingIcon} alt="UsersIcon" />}
        />

        <div className="content">
          {(user?.role === "customerService" || user.role === "companyManager") && (
            <Filter
              filters={filters}
              clear={clearFilters}
              submit={applyFilters}
              inputChange={inputChange}
              expand={handleFilterPanel}
              expandFilter={user.role !== "student" ? true : false}
            />
          )}

          {classesRatings?.length > 0 ? (
            <Next>
              <div className="container">
                {classesRatings?.map((item, index) => (
                  <div key={index} className="nextClass">
                    <div className="boxClass">
                      <div className="date">
                        <h3>{item.timeTotal[0] + item.timeTotal[1]}</h3>
                      </div>

                      <div className="infos">
                        <div className="classContent">
                          <h4>
                            <FlagIcon size={25} code={item?.lingoLanguage.flag} />
                            {t(decryptIfEncrypted(item?.lingoLanguage?.language?.name)?.toUpperCase())}
                          </h4>

                          <div className="classAndTeacher">
                            <div>
                              <div>
                                <h4>
                                  {t("CARD_CLASS_STUDENT")}:{" "}
                                  {item?.student.user.name ? (
                                    decryptIfEncrypted(item?.student.user.name)
                                  ) : (
                                    <div
                                      dangerouslySetInnerHTML={{ __html: "&nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;" }}
                                    />
                                  )}
                                  &nbsp; • &nbsp;
                                  {t("CARD_CLASS_CONTENT")}:{" "}
                                  {item?.classScheduleDetails ? (
                                    t("BTN_" + item?.classScheduleDetails[0].focus.toUpperCase())
                                  ) : (
                                    <div
                                      dangerouslySetInnerHTML={{ __html: "&nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;" }}
                                    />
                                  )}
                                </h4>
                              </div>

                              <h4>
                                {t("CARD_CLASS_TEACHER")}:{" "}
                                {item?.teacher ? (
                                  decryptIfEncrypted(item?.teacher.user.name)
                                ) : (
                                  <div dangerouslySetInnerHTML={{ __html: "&nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;" }} />
                                )}
                              </h4>
                            </div>
                          </div>

                          <h4>
                            {t("CARD_PLAN_LEVEL")}:
                            {item?.student?.studentLevelGrades ? (
                              <span>
                                {
                                  item?.student?.studentLevelGrades[item?.student?.studentLevelGrades?.length - 1]
                                    ?.level?.level
                                }
                              </span>
                            ) : (
                              <div dangerouslySetInnerHTML={{ __html: "&nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;" }} />
                            )}
                          </h4>

                          {item?.teacherRating && item?.teacherRating?.status === "done" ? (
                            <div className="status">
                              {verifiedDate(item.originalScheduledDateTime, dateVerified) && (
                                <div className="status">
                                  <button className="update" onClick={(e) => openRating(e, index)}>
                                    {t("EDIT")} <i className="fa fa-angle-right" aria-hidden="true"></i>
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="status">
                              {verifiedDate(item.originalScheduledDateTime, dateVerified) && (
                                <button className="active" onClick={(e) => openRating(e, index)}>
                                  {t("RATE")} <i className="fa fa-angle-right" aria-hidden="true"></i>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Pagination
                totalPages={pagination.totalPages}
                pageCurrent={pagination.pageNumber}
                totalResults={pagination.totalFound}
                totalFound={pagination.pageSize ?? 20}
                onClick={(page, type) => handlePagination(page, type)}
              />
            </Next>
          ) : (
            <Placeholder label="No results" />
          )}

          {classesForRating?.length > 0 && ratingCriterias?.length > 0 && targetRating !== "" && (
            <ModalRating target={targetRating} updateScreen={applyFilters} />
          )}
        </div>
      </section>
    </div>
  );
}

export default ClassRatings;
